import { useState } from 'react';
import { X, UserPlus, Check, Users, Info } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../../components/ui/select';
import {
  inviteFamilyMember,
  HomeRole,
  ServiceType,
  ALL_SERVICE_TYPES,
  SERVICE_TYPE_LABELS,
} from '../../services/family.api';

interface InviteSheetProps {
  homeId: string;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ROLES: { value: HomeRole; label: string; desc: string }[] = [
  { value: 'owner', label: 'Co-Owner', desc: 'Full access to all current & future assets and services' },
  { value: 'member', label: 'Member', desc: 'Can view and manage assigned assets & services' },
  { value: 'viewer', label: 'Viewer', desc: 'Read-only access to assigned assets' },
];

// Relation options — mirrors the mobile app's relation dropdown.
const RELATIONS = ['Spouse', 'Parent', 'Child', 'Sibling', 'Caretaker', 'Tenant', 'Other'];

export function InviteSheet({ homeId, open, onClose, onSuccess }: InviteSheetProps) {
  const [email, setEmail]             = useState('');
  const [name, setName]               = useState('');
  const [relation, setRelation]       = useState('');
  const [role, setRole]               = useState<HomeRole>('member');
  const [grantFuture, setGrantFuture] = useState(false);
  const [services, setServices]       = useState<ServiceType[]>([...ALL_SERVICE_TYPES]);
  const [saving, setSaving]           = useState(false);
  const [nameError, setNameError]     = useState('');
  const [emailError, setEmailError]   = useState('');

  // Co-owners get full access, so the per-service / future-asset controls
  // don't apply — matches the mobile app behaviour.
  const isCoOwner = role === 'owner';

  if (!open) return null;

  const toggleService = (s: ServiceType) =>
    setServices((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let hasError = false;
    setNameError('');
    setEmailError('');

    if (!name.trim()) { setNameError('Full name is required'); hasError = true; }
    if (!email.trim()) { setEmailError('Email is required'); hasError = true; }
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setEmailError('Enter a valid email address'); hasError = true; }
    if (hasError) return;

    setSaving(true);
    try {
      await inviteFamilyMember({
        homeId,
        email: email.trim(),
        role,
        inviteeName: name.trim(),
        relation: relation.trim() || undefined,
        // Co-owners implicitly get everything, so future access is always on
        // and asset/service scoping is left unset.
        grantFutureAssets: isCoOwner ? true : grantFuture,
        serviceTypes: isCoOwner ? undefined : services,
      });
      onSuccess();
    } catch (err: unknown) {
      setEmailError(err instanceof Error ? err.message : 'Failed to send invite');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Sheet — top-14 matches the h-14 navbar exactly */}
      <div className="fixed inset-x-0 bottom-0 z-50 sm:inset-auto sm:right-0 sm:top-14 sm:bottom-0 sm:w-[420px] flex flex-col bg-background border-l shadow-2xl rounded-t-2xl sm:rounded-none">

        {/* Header */}
        <div className="border-b bg-muted/30 shrink-0">
          <div className="flex items-center justify-between px-5 py-4">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center">
                <Users className="h-4 w-4 text-primary" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-foreground">Invite Member</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Add someone to your household</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="px-6 py-5 space-y-4 flex-1 overflow-y-auto min-h-0">

            {/* Name — required */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">
                Full Name <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => { setName(e.target.value); setNameError(''); }}
                placeholder="Their full name"
                className={cn(
                  'w-full h-9 px-3 text-sm rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground transition-colors',
                  nameError ? 'border-destructive focus:ring-destructive/30' : 'border-border'
                )}
              />
              {nameError && <p className="text-xs text-destructive">{nameError}</p>}
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">
                Email Address <span className="text-destructive">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
                placeholder="name@example.com"
                className={cn(
                  'w-full h-9 px-3 text-sm rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground transition-colors',
                  emailError ? 'border-destructive focus:ring-destructive/30' : 'border-border'
                )}
              />
              {emailError && <p className="text-xs text-destructive">{emailError}</p>}
            </div>

            {/* Relation */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">
                Relation <span className="text-muted-foreground font-normal">(optional)</span>
              </label>
              <Select
                value={relation === '' ? 'none' : relation}
                onValueChange={(v) => setRelation(v === 'none' ? '' : v)}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Select a relation" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {RELATIONS.map((rel) => (
                    <SelectItem key={rel} value={rel}>{rel}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Role */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">Role</label>
              <div className="space-y-2">
                {ROLES.map((r) => (
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => setRole(r.value)}
                    className={cn(
                      'w-full flex items-start gap-3 px-3.5 py-3 rounded-xl border text-left transition-all',
                      role === r.value
                        ? 'border-primary/50 bg-primary/5'
                        : 'border-border hover:border-primary/30 hover:bg-muted/30'
                    )}
                  >
                    <div className={cn(
                      'mt-0.5 h-4 w-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors',
                      role === r.value ? 'border-primary bg-primary' : 'border-muted-foreground'
                    )}>
                      {role === r.value && <div className="h-1.5 w-1.5 rounded-full bg-background" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{r.label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{r.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Co-owners get full access — no per-service / future-asset choices needed */}
            {isCoOwner ? (
              <div className="flex items-start gap-3 p-4 rounded-xl bg-primary/5 border border-primary/20">
                <Info className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">Full Access — Co-Owner</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Co-owners automatically get access to all current and future assets and every service in this home.
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Service access */}
                <div className="space-y-2">
                  <div>
                    <label className="text-xs font-medium text-foreground">Service Access</label>
                    <p className="text-xs text-muted-foreground mt-0.5">Choose which services this person can use</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {ALL_SERVICE_TYPES.map((s) => {
                      const checked = services.includes(s);
                      return (
                        <button
                          key={s}
                          type="button"
                          onClick={() => toggleService(s)}
                          className={cn(
                            'flex items-center gap-2 px-3 py-2.5 rounded-lg border text-xs font-medium transition-all text-left',
                            checked
                              ? 'border-primary/50 bg-primary/5 text-foreground'
                              : 'border-border text-muted-foreground hover:border-primary/30 hover:bg-muted/30'
                          )}
                        >
                          <div className={cn(
                            'h-4 w-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors',
                            checked ? 'border-primary bg-primary' : 'border-muted-foreground'
                          )}>
                            {checked && <Check className="h-2.5 w-2.5 text-background" />}
                          </div>
                          {SERVICE_TYPE_LABELS[s]}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Auto-share future assets */}
                <button
                  type="button"
                  onClick={() => setGrantFuture((v) => !v)}
                  className={cn(
                    'w-full flex items-start gap-3 px-3.5 py-3 rounded-xl border text-left transition-all',
                    grantFuture ? 'border-primary/50 bg-primary/5' : 'border-border hover:border-primary/30 hover:bg-muted/30'
                  )}
                >
                  <div className={cn(
                    'mt-0.5 h-4 w-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors',
                    grantFuture ? 'border-primary bg-primary' : 'border-muted-foreground'
                  )}>
                    {grantFuture && <Check className="h-2.5 w-2.5 text-background" />}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Auto-share future assets</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Automatically grant access to assets added to this home in the future
                    </p>
                  </div>
                </button>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t bg-muted/20 flex gap-3 shrink-0">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" className="flex-1 gap-1.5" disabled={saving}>
              {saving ? (
                <span className="h-3.5 w-3.5 border-2 border-background/40 border-t-background rounded-full animate-spin" />
              ) : (
                <UserPlus className="h-3.5 w-3.5" />
              )}
              {saving ? 'Sending…' : 'Send Invite'}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
}
